/**
 * Input — form-field atom with four behaviour modes.
 *
 * Wraps a native form control with a styled container that owns the
 * border, padding, and icon slots. State (hover, focus, error, disabled)
 * is driven by real DOM selectors — no `state` prop, so consumers don't
 * have to fight the native behaviour.
 *
 * `mode` picks the high-level shape:
 *   - `text` (default)  — standard text entry. Owns the `type` sub-prop
 *                         (text / email / password / etc.).
 *   - `trigger`         — the whole Shell acts as a button. `onClick`
 *                         fires on activation, input is forced readOnly,
 *                         right icon is decorative. Use for dropdown /
 *                         picker / select triggers.
 *   - `textarea`        — multi-line `<textarea>`. Same chrome, taller
 *                         body, `rows` controls initial height.
 *   - `segmented`       — fixed-length sequence split into single-char
 *                         cells with auto-advance, backspace navigation,
 *                         and paste distribution. Use for OTP / PIN /
 *                         verification codes. `length` controls cell
 *                         count (default 6).
 *
 * Sizes:
 *   s — 32 height, body.m text, 16 icon
 *   m — 36 height, body.l text, 20 icon (DEFAULT)
 *   l — 48 height, heading.m text, 20 icon
 *
 * Right-slot precedence (text mode only; trigger/textarea/segmented have
 * their own slot rules — see JSDoc per prop):
 *   1. `type === 'password'`     — automatic eye-toggle.
 *   2. `clearable && hasValue`   — × clear button.
 *   3. `onRightIconClick` set    — custom button.
 *   4. otherwise                 — `rightIcon` as decoration.
 *
 * @format
 */

import {
  forwardRef,
  isValidElement,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type MouseEventHandler,
  type ReactNode,
  type Ref,
  type TextareaHTMLAttributes,
} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {text, type TypographyRole} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import bordersTokens from 'ui/tokens/semantic/borders.json';
import {v} from 'ui/tokens/util';

import {Icon} from '../Icon';

const RADIUS_M_PX = parseInt(v(bordersTokens.radius.m));

/**
 * Public enums. Stories import these arrays directly so the control
 * options auto-sync when an enum changes. Types are derived from the
 * arrays via `(typeof X)[number]` — single source of truth.
 *
 * `INPUT_SIZES` — named height steps (32 / 36 / 48). Intentionally differ
 * from Button's 32 / 48 / 64.
 *
 * `INPUT_TEXT_TYPES` — HTML input types accepted in `mode='text'`. They
 * determine the mobile keyboard layout, autofill behaviour, and built-in
 * browser validation. See Input.mdx for a per-value rationale.
 *
 * `INPUT_MODES` — top-level behaviour switch.
 */
export const INPUT_SIZES = ['s', 'sm', 'm', 'l'] as const;
export const INPUT_TEXT_TYPES = [
  'text',
  'email',
  'tel',
  'password',
  'number',
  'url',
  'search',
] as const;
export const INPUT_MODES = ['text', 'segmented', 'textarea', 'trigger'] as const;

export type InputSize = (typeof INPUT_SIZES)[number];
export type InputTextType = (typeof INPUT_TEXT_TYPES)[number];
export type InputMode = (typeof INPUT_MODES)[number];

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  /**
   * **Modes: all.** High-level behaviour of the field.
   *  - `text` (default) — text entry. Owns `type`.
   *  - `trigger` — clickable button trigger. `onClick` fires on
   *    activation, input is forced `readOnly`, right icon is decorative.
   *  - `textarea` — multi-line. Use `rows` to set initial height.
   *  - `segmented` — OTP / verification cells. Use `length` to set cell count.
   */
  mode?: InputMode;

  /** **Modes: all.** Height + typography + icon size. */
  size?: InputSize;

  /**
   * **Modes: text only.** HTML input type. Ignored in `trigger`,
   * `textarea`, `segmented`. `password` automatically renders an eye-toggle
   * in the right slot.
   */
  type?: InputTextType;

  /**
   * **Modes: text, trigger, textarea.** Placeholder text. Ignored in
   * `segmented` — empty cells render `segmentPlaceholder` instead.
   */
  placeholder?: string;

  /**
   * **Modes: text, trigger, textarea.** Left icon — Material Symbols
   * ligature name or React element. Ignored in `segmented` (cells own the
   * full chrome).
   */
  leftIcon?: string | ReactNode;

  /**
   * **Modes: text, trigger, textarea.** Right icon. Overridden in `text`
   * mode when `type="password"` (eye-toggle wins) or when `clearable`
   * is on and the field has a value (× wins). In `trigger` mode the
   * right icon is always decorative (no click handler). Ignored in
   * `segmented` mode.
   */
  rightIcon?: string | ReactNode;

  /**
   * **Modes: text only.** Click handler for the right icon — makes the
   * right slot a real `<button>`. Ignored in other modes and when
   * password / clearable take the slot.
   */
  onRightIconClick?: (event: MouseEvent<HTMLButtonElement>) => void;

  /**
   * **Modes: text only.** Доступное имя кнопки в правом слоте. Без него
   * скринридер читает её как «Action» — это верно по типу, но ничего не
   * говорит о действии («Открыть календарь»).
   */
  rightIconLabel?: string;

  /**
   * **Modes: text only.** When the field has a value, the right slot
   * displays a close (×) icon that clears the field on click.
   * Overrides any `rightIcon` while the field is non-empty. Ignored
   * on `type="password"` (eye-toggle owns the slot) and in `trigger`,
   * `textarea`, `segmented` modes. **Default `true`** — most text inputs
   * benefit from a quick-clear affordance.
   */
  clearable?: boolean;

  /**
   * **Modes: textarea only.** Initial number of visible rows. Default 3.
   * The textarea grows beyond `rows` if the user drags the corner
   * resize handle (anchored at the field's bottom-right corner).
   */
  rows?: number;

  /**
   * **Modes: segmented only.** Number of cells. Default 6. Auto-advance
   * fires after each cell is filled; backspace on an empty cell focuses
   * the previous one; paste distributes characters across cells starting
   * from the focused one.
   */
  length?: number;

  /**
   * **Modes: segmented only.** Placeholder shown in every empty cell.
   * Defaults to a Material Symbols `asterisk` glyph (rendered via the
   * `Icon` atom) so the field reads as a masked code before anything is
   * typed. Override to change the hint per design:
   *   - a **string** renders as a literal symbol (`'•'`, `'_'`, `'#'`),
   *   - a **React element** renders as-is — pass another
   *     `<Icon name="lock" size="s" color="on-surface-muted" />` for a
   *     different glyph, or any custom node.
   * The placeholder is decorative (`aria-hidden`), disappears the moment
   * a cell is filled, and is tinted `on-surface-muted`.
   */
  segmentPlaceholder?: ReactNode;

  /** **Modes: all.** Error flag. `true` paints border + left icon in `bad/bad`. */
  error?: boolean;

  /** **Modes: all.** Error message below the field. Truthy value implies error state. */
  errorMessage?: string;

  /** **Modes: all.** Helper text below the field when there's no error. */
  helperText?: string;

  /** **Modes: all.** Stretches to fill the container width. */
  fullWidth?: boolean;

  /**
   * **Modes: all.** Whether to round the corners (`radius.m`). Default
   * `true`. Set to `false` for segmented input groups / overflow-clipped
   * containers. In `segmented` mode applies to each cell individually.
   */
  rounded?: boolean;
}

// ── Size matrix ─────────────────────────────────────────────────────────────

interface SizeStyle {
  height: string;
  textRole: TypographyRole;
  iconSize: 'xs' | 's' | 'm';
  errorMessageRole: TypographyRole;
}

const SIZE: Record<InputSize, SizeStyle> = {
  s: {height: '32px', textRole: 'body.m', iconSize: 'xs', errorMessageRole: 'body.s'},
  sm: {height: '36px', textRole: 'body.l', iconSize: 's', errorMessageRole: 'body.s'},
  m: {height: '48px', textRole: 'body.l', iconSize: 's', errorMessageRole: 'body.s'},
  l: {height: '64px', textRole: 'heading.m', iconSize: 's', errorMessageRole: 'body.m'},
};

// ── Styled chrome ────────────────────────────────────────────────────────

interface ShellProps {
  $size: InputSize;
  $fullWidth: boolean;
  $rounded: boolean;
  $trigger: boolean;
  $textarea: boolean;
}

const Shell = styled('label', {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<ShellProps>(({theme, $size, $fullWidth, $rounded, $trigger, $textarea}) => {
  const size = SIZE[$size];
  const radius = $rounded ? RADIUS_M_PX : 0;

  return {
    display: 'inline-flex',
    alignItems: $textarea ? 'flex-start' : 'center',
    gap: theme.spacing(2),
    // Textarea Shell padding is asymmetric: 8px on top + left only,
    // ZERO on right + bottom. This lets the textarea fill all the way
    // to the Shell's right and bottom borders, so the browser's native
    // resize handle (rendered at the textarea's bottom-right corner)
    // sits flush with the visible field corner — grippable at the
    // exact pixel of the border. Breathing room for text is moved
    // INTO the textarea via its own padding instead. Right-slot icon
    // is absolutely positioned (see `TextareaRightSlot`), so the
    // textarea owns the full inner area uncontested.
    ...($textarea
      ? {minHeight: size.height, padding: theme.spacing(2, 0, 0, 2), position: 'relative'}
      : {height: size.height, paddingLeft: theme.spacing(2), paddingRight: theme.spacing(2)}),
    boxSizing: 'border-box',
    width: $fullWidth ? '100%' : 'auto',
    borderRadius: radius,

    border: `1px solid ${surfaceVar('onSurfaceMuted')}`,
    backgroundColor: 'transparent',
    color: surfaceVar('onSurface'),

    transition: theme.transitions.create(['border-color', 'color'], {
      duration: theme.transitions.duration.shorter,
    }),

    cursor: $trigger ? 'pointer' : 'text',

    '&:hover': {borderColor: theme.palette.primary.hover},
    '&:focus-within': {borderColor: theme.palette.primary.active},
    '&[aria-invalid="true"]': {
      borderColor: theme.palette.bad.main,
      color: theme.palette.bad.main,
    },
    '&[data-disabled="true"]': {
      borderColor: theme.palette.disabledStates.low,
      color: theme.palette.disabledStates.high,
      cursor: 'not-allowed',
      pointerEvents: 'none',
    },
  };
});

const NativeInput = styled('input', {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$size: InputSize}>(({theme, $size}) => {
  const size = SIZE[$size];
  return {
    flex: '1 1 auto',
    minWidth: 0,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    color: 'inherit',
    ...text(size.textRole),

    '&::placeholder': {color: surfaceVar('onSurfaceLight')},
    '&:focus::placeholder': {color: surfaceVar('onSurfaceMuted')},
    '&:disabled': {cursor: 'not-allowed', WebkitTextFillColor: theme.palette.disabledStates.high},
    '&[readonly]': {cursor: 'inherit'},

    '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active':
      {
        WebkitBoxShadow: `0 0 0 1000px ${surfaceVar('surface1')} inset`,
        WebkitTextFillColor: surfaceVar('onSurface'),
        caretColor: surfaceVar('onSurface'),
        transition: 'background-color 600000s 0s, color 600000s 0s',
      },
  };
});

const NativeTextarea = styled('textarea', {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$size: InputSize; $hasRightSlot: boolean}>(({theme, $size, $hasRightSlot}) => {
  const size = SIZE[$size];
  return {
    flex: '1 1 auto',
    minWidth: 0,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    boxSizing: 'border-box',
    // Internal padding owns the breathing room from the right + bottom
    // edges, because the Shell has zero padding on those sides (so the
    // resize handle can reach the visible corner). Top + left are zero
    // here because the Shell already pads those sides — Shell's
    // padding-top aligns ALL flex children (left icon, textarea text,
    // right slot) on the same horizontal baseline.
    paddingTop: 0,
    paddingLeft: 0,
    paddingBottom: theme.spacing(2),
    // Right padding includes icon width (~20) + breathing room (8) = 28;
    // 32 with slot for a small visual buffer; 8 without slot to keep
    // text off the border.
    paddingRight: $hasRightSlot ? theme.spacing(8) : theme.spacing(2),
    margin: 0,
    color: 'inherit',
    resize: 'vertical', // Allow user vertical resize; suppress horizontal.
    width: '100%',
    // The vertical resize handle uses inline style.height to shrink the
    // textarea. Without an explicit minHeight, users can drag below the
    // field's natural row height — content clips, the resize handle ends
    // up inside the form bounds, and an internal scrollbar appears.
    //
    // Math: total visible Shell height at minimum = size.height. With
    // `box-sizing: border-box`, Shell.minHeight INCLUDES its 1+1px
    // borders. Available content area inside Shell = size.height - 2
    // (borders) - 8 (Shell.padding-top) = size.height - 10. Textarea is
    // a flex child filling this area, so its own min-height must equal
    // size.height - 10. With this, Shell at floor = textarea(size-10) +
    // 8 padding-top + 2 borders = exactly size.height — same row height
    // as a single-line Input across sizes.
    minHeight: `${parseInt(size.height) - 10}px`,
    ...text(size.textRole),

    '&::placeholder': {color: surfaceVar('onSurfaceLight')},
    '&:focus::placeholder': {color: surfaceVar('onSurfaceMuted')},
    '&:disabled': {
      cursor: 'not-allowed',
      WebkitTextFillColor: theme.palette.disabledStates.high,
      resize: 'none',
    },

    // Scrollbar styling — slim thumb running the full track height with
    // no arrow buttons (Chrome / Edge / Safari default scrollbar has up
    // / down step buttons that create visible gaps at the ends). This
    // is a tactical fix; a proper Scrollbar molecule will replace these
    // rules once we lift scrolling into a reusable component.
    '&::-webkit-scrollbar': {width: '8px', height: '8px'},
    '&::-webkit-scrollbar-track': {background: 'transparent', border: 'none'},
    '&::-webkit-scrollbar-thumb': {
      background: surfaceVar('onSurfaceMuted'),
      borderRadius: '4px',
      border: 'none',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: theme.palette.primary.hover,
    },
    // Hide step buttons aggressively — `display: none` alone is enough
    // on most Chromium builds, but some Windows builds still reserve
    // the box; zeroing size + targeting every button sub-selector kills
    // it everywhere.
    '&::-webkit-scrollbar-button': {display: 'none', width: 0, height: 0},
    '&::-webkit-scrollbar-button:single-button': {display: 'none'},
    '&::-webkit-scrollbar-button:double-button': {display: 'none'},
    '&::-webkit-scrollbar-button:vertical:start': {display: 'none'},
    '&::-webkit-scrollbar-button:vertical:end': {display: 'none'},
    '&::-webkit-scrollbar-button:vertical:decrement': {display: 'none'},
    '&::-webkit-scrollbar-button:vertical:increment': {display: 'none'},
    '&::-webkit-scrollbar-corner': {background: 'transparent'},
    // The resizer is Chrome's diagonal-grip box at the bottom-right
    // corner. By default it has its own background tint that shows up
    // as an outline against our transparent textarea — especially
    // visible the moment a scrollbar arrives next to it. Transparent
    // background flattens it into the field, and a tiny linear-gradient
    // keeps a subtle 2-stripe gripper so the affordance survives.
    '&::-webkit-resizer': {
      background: 'transparent',
      backgroundImage: `linear-gradient(135deg, transparent 0 55%, ${surfaceVar('onSurfaceMuted')} 55% 65%, transparent 65% 75%, ${surfaceVar('onSurfaceMuted')} 75% 85%, transparent 85%)`,
      backgroundClip: 'padding-box',
    },
    // Firefox — no per-part styling available, but it doesn't render
    // arrow buttons anyway, so `thin` is enough to match the visual.
    scrollbarWidth: 'thin',
    scrollbarColor: `${surfaceVar('onSurfaceMuted')} transparent`,
  };
});

const TextareaRightSlot = styled(Box)({
  // Pulled out of flex flow so the textarea fills the full inner width
  // and its resize handle sits at the Shell's bottom-right corner.
  // Top-aligned with the first line of text.
  position: 'absolute',
  top: '8px',
  right: '8px',
  display: 'inline-flex',
  alignItems: 'center',
});

const Message = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$size: InputSize; $tone: 'error' | 'helper'}>(({theme, $size, $tone}) => {
  const role = SIZE[$size].errorMessageRole;
  return {
    ...text(role),
    color: $tone === 'error' ? theme.palette.bad.main : surfaceVar('onSurfaceMuted'),
    marginTop: theme.spacing(1),
  };
});

const Wrapper = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$fullWidth: boolean}>(({$fullWidth}) => ({
  display: 'inline-flex',
  flexDirection: 'column',
  width: $fullWidth ? '100%' : 'auto',
}));

const RightSlotButton = styled('button')({
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  display: 'inline-flex',
  color: 'inherit',
});

// Invisible placeholder that reserves the right-slot's space when no
// interactive button is currently shown. Prevents layout shift in text
// mode with `clearable=true`, where the × button appears as soon as the
// user types and disappears when the field is cleared. The spacer takes
// the exact dimensions an icon would, so the input's visible width is
// constant regardless of whether the clear button is rendered.
const RightSlotSpacer = styled(Box)({
  display: 'inline-flex',
  visibility: 'hidden',
  pointerEvents: 'none',
});

// ── Segmented mode: shared-chrome cells row ──────────────────────────────
//
// Visual model: one continuous bordered container with vertical dividers
// between cells (NOT N separate squares with gaps). The container owns
// the border, radius, and full state cycle (hover / focus-within / error
// / disabled). Each cell is a `CellWrap` box carrying a 1px left divider
// (skipped on the first cell) and holding two stacked children: an
// absolutely-positioned placeholder overlay and the borderless `<input>`
// on top. `overflow: hidden` on the container clips cells to the rounded
// corners.
//
// Why the wrapper (vs a bare input like before): a native `<input>`
// placeholder can only be plain text, but the design calls for an icon
// (asterisk) — or an arbitrary symbol / custom glyph — in each empty
// cell. An overlay behind the transparent input gives us a real
// component slot for that hint while the input keeps ownership of the
// caret, value, and keyboard behaviour.
//
// State is driven by two inherited CSS custom properties set on the
// container per state, so border, dividers, and cell fill always move as
// one palette:
//   --seg-line — the line colour, consumed by BOTH the container border
//                and every cell divider (they always match exactly):
//                on-surface-muted at rest, primary on hover/focus, bad on
//                error, disabled-low when disabled.
//   --seg-fill — the fill of FILLED cells, tracking the same palette as
//                the border: surface-2 at rest (surface border) →
//                primary-container on hover/focus (primary border) →
//                transparent on error / disabled (uniform state read).

const CellWrap = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$size: InputSize; $filled: boolean}>(({theme, $size, $filled}) => {
  const size = SIZE[$size];
  return {
    position: 'relative',
    width: size.height, // square cell matching control height
    height: '100%',
    boxSizing: 'border-box',
    display: 'inline-flex',

    // Filled cells fill with `--seg-fill` (the container tint of the
    // active border palette); empty cells never fill. Animate both the
    // fill and the divider colour so typing / state changes are smooth.
    backgroundColor: $filled ? 'var(--seg-fill)' : 'transparent',
    transition: theme.transitions.create(['background-color', 'border-color'], {
      duration: theme.transitions.duration.shorter,
    }),

    // Vertical divider — every cell except the first carries a 1px left
    // border that reads `--seg-line`, the SAME custom property the
    // container border uses, so dividers and border always match.
    '&:not(:first-of-type)': {
      borderLeft: '1px solid var(--seg-line)',
    },
  };
});

const CellsRow = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$size: InputSize; $rounded: boolean}>(({theme, $size, $rounded}) => {
  const size = SIZE[$size];
  const radius = $rounded ? RADIUS_M_PX : 0;
  return {
    display: 'inline-flex',
    height: size.height,
    boxSizing: 'border-box',

    // Resting palette: surface border + surface-2 fill. State selectors
    // below only swap these two custom properties — the border, dividers
    // (CellWrap reads `--seg-line`), and filled-cell fill (CellWrap reads
    // `--seg-fill`) all follow automatically.
    '--seg-line': surfaceVar('onSurfaceMuted'),
    '--seg-fill': surfaceVar('surface2'),

    border: '1px solid var(--seg-line)',
    borderRadius: radius,
    overflow: 'hidden', // clip cell corners to container radius
    backgroundColor: 'transparent',
    color: surfaceVar('onSurface'),
    transition: theme.transitions.create(['border-color', 'color'], {
      duration: theme.transitions.duration.shorter,
    }),

    // Hover / focus → primary border + primary-container fill.
    '&:hover': {
      '--seg-line': theme.palette.primary.hover,
      '--seg-fill': theme.palette.primary.container,
    },
    '&:focus-within': {
      '--seg-line': theme.palette.primary.active,
      '--seg-fill': theme.palette.primary.container,
    },
    // Error / disabled come last so they win over hover/focus, and drop
    // the fill so the field reads as a single uniform state.
    '&[aria-invalid="true"]': {
      '--seg-line': theme.palette.bad.main,
      '--seg-fill': 'transparent',
      color: theme.palette.bad.main,
    },
    '&[data-disabled="true"]': {
      '--seg-line': theme.palette.disabledStates.low,
      '--seg-fill': 'transparent',
      color: theme.palette.disabledStates.high,
      cursor: 'not-allowed',
      pointerEvents: 'none',
    },
  };
});

// Placeholder overlay for an empty cell. Sits behind the transparent
// input (z-index 0 vs the input's 1) so the caret stays on top, is
// non-interactive (clicks fall through to the input), and is tinted
// `on-surface-muted`. A string child renders as a literal symbol at the
// cell's typographic size; a React element (e.g. an `Icon`) renders
// as-is.
const CellPlaceholder = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$size: InputSize}>(({$size}) => {
  const size = SIZE[$size];
  return {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    color: surfaceVar('onSurfaceMuted'),
    ...text(size.textRole),
  };
});

const SegmentedCell = styled('input', {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$size: InputSize}>(({theme, $size}) => {
  const size = SIZE[$size];
  return {
    position: 'relative',
    zIndex: 1, // keep the input (and its caret) above the placeholder overlay
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    textAlign: 'center',
    border: 'none',
    background: 'transparent',
    color: 'inherit',
    padding: 0,
    margin: 0,
    outline: 'none',
    ...text(size.textRole),

    '&:disabled': {
      cursor: 'not-allowed',
      WebkitTextFillColor: theme.palette.disabledStates.high,
    },
  };
});

// ── Icon slot rendering ─────────────────────────────────────────────────

function renderIconSlot(
  icon: string | ReactNode | undefined,
  iconSize: 'xs' | 's' | 'm',
  isError: boolean,
  isDisabled: boolean,
): ReactNode {
  if (!icon) return null;
  if (typeof icon === 'string') {
    const color = isDisabled ? 'disabled' : isError ? 'bad' : 'on-surface-muted';
    return <Icon name={icon} size={iconSize} color={color} />;
  }
  if (isValidElement(icon)) return icon;
  return icon;
}

// ── Segmented mode subcomponent ──────────────────────────────────────────

interface SegmentedCellsProps {
  length: number;
  size: InputSize;
  rounded: boolean;
  error: boolean;
  disabled: boolean;
  id?: string;
  value?: string;
  defaultValue?: string;
  placeholder?: ReactNode;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}

function SegmentedInputCells({
  length,
  size,
  rounded,
  error,
  disabled,
  id,
  value,
  defaultValue,
  placeholder,
  onChange,
}: SegmentedCellsProps) {
  const initialString = String(value ?? defaultValue ?? '').slice(0, length);
  const initialCells = (() => {
    const arr = initialString.split('');
    while (arr.length < length) arr.push('');
    return arr;
  })();
  const [cells, setCells] = useState<string[]>(initialCells);
  const cellRefs = useRef<(HTMLInputElement | null)[]>([]);
  // Always-current logical value. `commit` updates it synchronously BEFORE
  // moving focus, so the focus-event handler (`handleFocus`) never reads a
  // stale `cells` state — that stale read snapped focus back to cell 0 after
  // the first digit and scrambled typing order (910 → 109).
  const valueRef = useRef(initialString);

  // Controlled sync — when the parent's `value` changes externally,
  // mirror it into the local cells array.
  useEffect(() => {
    if (value === undefined) return;
    const str = String(value).slice(0, length);
    valueRef.current = str;
    const arr = str.split('');
    while (arr.length < length) arr.push('');
    setCells(arr);
  }, [value, length]);

  const fireChange = (combined: string, fromIndex: number) => {
    if (!onChange) return;
    // Synthesise an event whose target.value is the combined string.
    const target = cellRefs.current[fromIndex];
    if (!target) return;
    onChange({
      ...({} as ChangeEvent<HTMLInputElement>),
      target: {...target, value: combined} as HTMLInputElement,
      currentTarget: target,
    } as ChangeEvent<HTMLInputElement>);
  };

  // ── Text-like editing model ──────────────────────────────────────────
  //
  // The row behaves like ONE contiguous text field split into cells: the
  // logical value is `cells.join('')` (empties only ever trail), and the
  // focused cell index maps to a text caret. Every mutation — type, paste,
  // Backspace, Delete — rewrites that string with insert / delete + shift
  // semantics, so characters to the right of the caret move exactly as
  // they would in a normal input. Editing keys are handled in `keydown`
  // (preventDefault'd) so behaviour never depends on where the browser
  // happens to park the native caret inside a single-char cell — the old
  // source of "Backspace does nothing because the caret is left of the
  // digit", even on repeated presses.

  // Text-caret position for cell `i` given the current value string `v`.
  // The caret sits just before cell `i`'s character. The one ambiguous
  // spot is a full field with the last cell focused: there the caret
  // belongs AFTER the final character (position = length) so Backspace
  // removes it rather than the one before it.
  const caretFor = (i: number, v: string) =>
    v.length === length && i === length - 1 ? length : Math.min(i, v.length);

  // Commit a new logical value and move focus to the cell at `caret`.
  const commit = (nextValue: string, caret: number) => {
    const v = nextValue.slice(0, length);
    valueRef.current = v;
    const arr = v.split('');
    while (arr.length < length) arr.push('');
    setCells(arr);
    const focusIdx = Math.max(0, Math.min(caret, length - 1));
    cellRefs.current[focusIdx]?.focus();
    fireChange(v, focusIdx);
  };

  const handleKeyDown = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    const v = cells.join('');
    const caret = caretFor(i, v);

    switch (e.key) {
      case 'Backspace':
        e.preventDefault();
        if (caret > 0) {
          commit(v.slice(0, caret - 1) + v.slice(caret), caret - 1);
        } else if (v.length > 0) {
          // Caret at the field start (focused first cell) — nothing sits to
          // the left, but the user wants to clear cell 0. Delete its own
          // character so a single/last digit is removable and a fresh code
          // can be typed.
          commit(v.slice(1), 0);
        }
        return;
      case 'Delete':
        e.preventDefault();
        if (caret < v.length) commit(v.slice(0, caret) + v.slice(caret + 1), caret);
        return;
      case 'ArrowLeft':
        e.preventDefault();
        cellRefs.current[Math.max(0, i - 1)]?.focus();
        return;
      case 'ArrowRight':
        e.preventDefault();
        cellRefs.current[Math.min(i + 1, v.length, length - 1)]?.focus();
        return;
      case 'Home':
        e.preventDefault();
        cellRefs.current[0]?.focus();
        return;
      case 'End':
        e.preventDefault();
        cellRefs.current[Math.min(v.length, length - 1)]?.focus();
        return;
      default:
        break;
    }

    // Printable character → insert at the caret and shift the rest right.
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      commit(v.slice(0, caret) + e.key + v.slice(caret), caret + 1);
    }
  };

  // Fallback for input methods that don't surface a usable `keydown`
  // (mobile / IME) and for programmatic changes (tests, autofill). Desktop
  // typing is handled in `handleKeyDown`, which preventDefaults — so this
  // does not double-fire there.
  const handleChange = (i: number, raw: string) => {
    const v = cells.join('');
    const caret = Math.min(i, v.length);
    if (raw === '') {
      // The cell was cleared natively (e.g. mobile Backspace).
      if (i < v.length) commit(v.slice(0, i) + v.slice(i + 1), i);
      return;
    }
    // One char (typing) or many (autofill / paste) landed here — insert
    // at the caret with shift.
    commit(v.slice(0, caret) + raw + v.slice(caret), caret + raw.length);
  };

  const handlePaste = (i: number, e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    if (!text) return;
    const v = cells.join('');
    const caret = Math.min(i, v.length);
    commit(v.slice(0, caret) + text + v.slice(caret), caret + text.length);
  };

  // Clicking an empty cell past the value end snaps the caret to the first
  // empty cell, so the caret can't be stranded in a gap.
  const handleFocus = (i: number) => {
    const filledLen = valueRef.current.length;
    if (i > filledLen && filledLen < length) cellRefs.current[filledLen]?.focus();
  };

  // Default placeholder — a Material Symbols `asterisk` sized to the cell.
  // Colour comes from the `CellPlaceholder` wrapper (`on-surface-muted`),
  // so the glyph inherits it. A caller-supplied node (string symbol or
  // custom element) replaces this wholesale.
  const placeholderNode =
    placeholder === undefined ? (
      <Icon name='asterisk' size={SIZE[size].iconSize} color='on-surface-muted' />
    ) : (
      placeholder
    );

  return (
    <CellsRow
      id={id}
      $size={size}
      $rounded={rounded}
      aria-invalid={error || undefined}
      data-disabled={disabled || undefined}
    >
      {cells.map((char, i) => (
        <CellWrap key={i} $size={size} $filled={char !== ''}>
          {char === '' && (
            <CellPlaceholder $size={size} aria-hidden='true'>
              {placeholderNode}
            </CellPlaceholder>
          )}
          <SegmentedCell
            ref={el => {
              cellRefs.current[i] = el;
            }}
            $size={size}
            value={char}
            disabled={disabled}
            aria-invalid={error || undefined}
            maxLength={1}
            inputMode='numeric'
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            onChange={e => handleChange(i, e.target.value)}
            onKeyDown={e => handleKeyDown(i, e)}
            onPaste={e => handlePaste(i, e)}
            onFocus={() => handleFocus(i)}
          />
        </CellWrap>
      ))}
    </CellsRow>
  );
}

// ── Main component ──────────────────────────────────────────────────────

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    mode = 'text',
    size = 'm',
    type = 'text',
    leftIcon,
    rightIcon,
    onRightIconClick,
    rightIconLabel,
    clearable = true,
    rows = 3,
    length = 6,
    segmentPlaceholder,
    error = false,
    errorMessage,
    helperText,
    fullWidth = false,
    rounded = true,
    disabled = false,
    onChange,
    onClick,
    style,
    className,
    id,
    ...rest
  },
  ref,
) {
  const sizeStyle = SIZE[size];
  const isError = Boolean(error) || Boolean(errorMessage);
  const isTrigger = mode === 'trigger';
  const isTextarea = mode === 'textarea';
  const isSegmented = mode === 'segmented';
  const isText = mode === 'text';
  const isPassword = isText && type === 'password';

  // Hooks (must run unconditionally on every render — Rules of Hooks).
  // Several of these are only meaningful in text / trigger / textarea
  // modes, but we still need to call them when mode === 'segmented' to keep
  // the hook order stable across mode switches.
  const [revealed, setRevealed] = useState(false);
  const controlledValue = (rest as {value?: string | number | readonly string[]}).value;
  const defaultValue = (rest as {defaultValue?: string | number | readonly string[]}).defaultValue;
  const [internalHasValue, setInternalHasValue] = useState(() => String(defaultValue ?? '') !== '');
  const innerRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // Segmented mode — short-circuit; cells own their own layout.
  if (isSegmented) {
    return (
      <Wrapper $fullWidth={fullWidth} style={style} className={className}>
        <SegmentedInputCells
          length={length}
          size={size}
          rounded={rounded}
          error={isError}
          disabled={disabled}
          id={id}
          value={controlledValue !== undefined ? String(controlledValue) : undefined}
          defaultValue={defaultValue !== undefined ? String(defaultValue) : undefined}
          placeholder={segmentPlaceholder}
          onChange={onChange}
        />
        {errorMessage && (
          <Message id={`${id}-err`} role='alert' $size={size} $tone='error'>
            {errorMessage}
          </Message>
        )}
        {!isError && helperText && (
          <Message id={`${id}-help`} $size={size} $tone='helper'>
            {helperText}
          </Message>
        )}
      </Wrapper>
    );
  }

  // ── Text / trigger / textarea modes — share Shell chrome ──────────────

  const effectiveType = isPassword && revealed ? 'text' : type;
  const computedHasValue =
    controlledValue !== undefined ? String(controlledValue) !== '' : internalHasValue;
  const setRefs = (el: HTMLInputElement | HTMLTextAreaElement | null) => {
    innerRef.current = el;
    if (typeof ref === 'function') {
      (ref as (e: HTMLInputElement | null) => void)(el as HTMLInputElement | null);
    } else if (ref) {
      (ref as {current: HTMLInputElement | null}).current = el as HTMLInputElement | null;
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (disabled) return;
    setInternalHasValue(e.target.value !== '');
    onChange?.(e as ChangeEvent<HTMLInputElement>);
  };

  const handleClear = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const input = innerRef.current;
    if (!input) return;
    const proto = isTextarea ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
    setter?.call(input, '');
    input.dispatchEvent(new Event('input', {bubbles: true}));
    setInternalHasValue(false);
    input.focus();
  };

  // Right-slot precedence (trigger / segmented already short-circuited).
  let rightContent: ReactNode | string | undefined = rightIcon;
  let rightClickHandler: ((e: MouseEvent<HTMLButtonElement>) => void) | undefined;
  let rightAriaLabel: string | undefined;

  if (isTrigger) {
    // Decoration only.
  } else if (isPassword) {
    rightContent = revealed ? 'visibility_off' : 'visibility';
    rightClickHandler = e => {
      e.stopPropagation();
      e.preventDefault();
      setRevealed(r => !r);
    };
    rightAriaLabel = revealed ? 'Hide password' : 'Show password';
  } else if (isText && clearable && computedHasValue) {
    // `clearable` is text-mode only. In textarea / trigger / segmented
    // modes the right slot has different semantics (or no slot at all).
    rightContent = 'close';
    rightClickHandler = handleClear;
    rightAriaLabel = 'Clear';
  } else if (onRightIconClick) {
    rightClickHandler = onRightIconClick;
    rightAriaLabel = rightIconLabel ?? 'Action';
  }

  const leftNode = renderIconSlot(leftIcon, sizeStyle.iconSize, isError, disabled);
  const rightNode = renderIconSlot(rightContent, sizeStyle.iconSize, false, disabled);
  const hasRightSlot = Boolean(rightNode);

  // The interactive vs decorative right slot wrapper. Shared between
  // text/trigger (inline) and textarea (absolute) layouts.
  const rightSlotContent: ReactNode =
    rightNode && rightClickHandler ? (
      <RightSlotButton
        type='button'
        onClick={rightClickHandler}
        aria-label={rightAriaLabel ?? 'Action'}
      >
        {rightNode}
      </RightSlotButton>
    ) : (
      rightNode
    );

  // In trigger mode the Shell handles click; drop `htmlFor` so the
  // label-click→focus side-effect doesn't fire.
  const labelHtmlFor = isTrigger ? undefined : id;
  const shellOnClick = isTrigger ? onClick : undefined;

  return (
    <Wrapper $fullWidth={fullWidth} style={style} className={className}>
      <Shell
        $size={size}
        $fullWidth={fullWidth}
        $rounded={rounded}
        $trigger={isTrigger}
        $textarea={isTextarea}
        aria-invalid={isError || undefined}
        data-disabled={disabled || undefined}
        htmlFor={labelHtmlFor}
        onClick={shellOnClick as MouseEventHandler<HTMLLabelElement> | undefined}
      >
        {leftNode}
        {isTextarea ? (
          <NativeTextarea
            {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)}
            ref={setRefs}
            id={id}
            rows={rows}
            disabled={disabled}
            readOnly={rest.readOnly}
            onChange={handleChange}
            $size={size}
            $hasRightSlot={hasRightSlot}
            aria-describedby={errorMessage ? `${id}-err` : helperText ? `${id}-help` : undefined}
          />
        ) : (
          <NativeInput
            {...rest}
            ref={setRefs as Ref<HTMLInputElement>}
            id={id}
            type={effectiveType}
            disabled={disabled}
            readOnly={isTrigger || rest.readOnly}
            onChange={handleChange}
            $size={size}
            aria-describedby={errorMessage ? `${id}-err` : helperText ? `${id}-help` : undefined}
          />
        )}
        {/* Right-slot layout differs per mode: textarea uses absolute
            positioning so the textarea fills the full width and its
            resize handle reaches the Shell corner.
            In text mode with `clearable=true` we render an invisible
            spacer when the × button is not currently shown — preserves
            the input's visible width across empty/filled states. */}
        {isTextarea && rightSlotContent ? (
          <TextareaRightSlot>{rightSlotContent}</TextareaRightSlot>
        ) : rightSlotContent ? (
          rightSlotContent
        ) : isText && clearable ? (
          <RightSlotSpacer aria-hidden='true'>
            <Icon name='close' size={sizeStyle.iconSize} color='inherit' />
          </RightSlotSpacer>
        ) : null}
      </Shell>

      {errorMessage && (
        <Message id={`${id}-err`} role='alert' $size={size} $tone='error'>
          {errorMessage}
        </Message>
      )}
      {!isError && helperText && (
        <Message id={`${id}-help`} $size={size} $tone='helper'>
          {helperText}
        </Message>
      )}
    </Wrapper>
  );
});
